import React, { useEffect, useMemo, useState } from 'react';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded';
import FormatListNumberedRoundedIcon from '@material-ui/icons/FormatListNumberedRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import HelpOutlineRoundedIcon from '@material-ui/icons/HelpOutlineRounded';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import TimelineRoundedIcon from '@material-ui/icons/TimelineRounded';
import Navbar from '../../components/navBar/navBar';
import PageContainer from '../../components/PageContainer/PageContainer';
import { verifyLogin } from '../../helpers';
import { GUIDES, FAQS, MEDIA_BASE } from './helpContent';
import '../../assets/style/detailSection.scss';
import '../../assets/style/help.scss';

// Help: how-to guides (short video + numbered steps) and common questions,
// all from helpContent.js. A guide opens in place (#guide-id in the URL, so
// it can be linked); the search box filters guides and questions.

// "**bold**" -> <strong>bold</strong>
const rich = (text) => text.split(/\*\*(.+?)\*\*/g)
  // eslint-disable-next-line react/no-array-index-key
  .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const plain = (text) => text.replace(/\*\*/g, '').toLowerCase();
const guideText = (g) => plain([g.title, g.summary, ...(g.steps || []), g.tip || ''].join(' '));

function GuideVideo({ video, title }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className='help-video help-video-missing'>This video isn&apos;t available yet. The steps below cover the same.</div>;
  }
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      className='help-video'
      controls
      playsInline
      preload='metadata'
      poster={video.poster ? MEDIA_BASE + video.poster : undefined}
      src={MEDIA_BASE + video.file}
      aria-label={`Video: ${title}`}
      onError={() => setFailed(true)}
    />
  );
}

function GuideView({ guide, onBack, onSteps }) {
  return (
    <section className='ds-card help-guide'>
      <button type='button' className='help-back' onClick={onBack}>
        <ChevronLeftRoundedIcon />
        All guides
      </button>
      <h2>{guide.title}</h2>
      <p className='help-muted'>{guide.summary}</p>
      {guide.video ? <GuideVideo video={guide.video} title={guide.title} /> : null}
      <h3>Steps</h3>
      <ol className='help-steps'>
        {guide.steps.map((s) => <li key={s}>{rich(s)}</li>)}
      </ol>
      {guide.tip ? <p className='help-tip'>{rich(guide.tip)}</p> : null}
      {guide.page ? (
        <button type='button' className='help-action' onClick={onSteps}>
          <TimelineRoundedIcon />
          See the workflow steps in detail
        </button>
      ) : null}
    </section>
  );
}

// The end-to-end guide: wide card with two ways in (video / workflow page).
function FeaturedCard({ guide, onWatch, onSteps }) {
  const { video } = guide;
  return (
    <div className='help-card featured'>
      <button type='button' className='help-thumb' onClick={onWatch} aria-label={`Watch video: ${guide.title}`}>
        {video.poster ? <img src={MEDIA_BASE + video.poster} alt='' loading='lazy' onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
        <span className='help-thumb-icon'><PlayArrowRoundedIcon /></span>
        <span className='help-badge'>{`Video · ${video.duration}`}</span>
      </button>
      <span className='help-card-body'>
        <span className='help-start'>Start here</span>
        <strong>{guide.title}</strong>
        <span className='help-muted'>{guide.summary}</span>
        <span className='help-actions'>
          <button type='button' className='help-action primary' onClick={onWatch}>
            <PlayArrowRoundedIcon />
            Watch video
          </button>
          <button type='button' className='help-action' onClick={onSteps}>
            <TimelineRoundedIcon />
            See workflow steps
          </button>
        </span>
      </span>
    </div>
  );
}

function GuideCard({ guide, onOpen }) {
  const { video, page } = guide;
  let ThumbIcon = FormatListNumberedRoundedIcon;
  if (video) ThumbIcon = PlayArrowRoundedIcon;
  if (page) ThumbIcon = TimelineRoundedIcon;
  let badge = `${guide.steps.length} steps`;
  if (video) badge = `Video · ${video.duration}`;
  if (page) badge = `Workflow · ${guide.steps.length} steps`;
  return (
    <button type='button' className={`help-card${page ? ' featured' : ''}`} onClick={onOpen}>
      <span className='help-thumb'>
        {video && video.poster ? <img src={MEDIA_BASE + video.poster} alt='' loading='lazy' onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
        <span className='help-thumb-icon'><ThumbIcon /></span>
        <span className='help-badge'>{badge}</span>
      </span>
      <span className='help-card-body'>
        {page ? <span className='help-start'>Start here</span> : null}
        <strong>{guide.title}</strong>
        <span className='help-muted'>{guide.summary}</span>
      </span>
    </button>
  );
}

export default function Help(props) {
  verifyLogin();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(() => window.location.hash.slice(1));

  useEffect(() => {
    const onHash = () => setOpenId(window.location.hash.slice(1));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const open = (id) => {
    window.location.hash = id;
    window.scrollTo({ top: 0 });
  };
  const back = () => {
    window.history.pushState(null, '', window.location.pathname);
    setOpenId('');
  };

  const q = query.trim().toLowerCase();
  const guides = useMemo(() => GUIDES.filter((g) => !q || guideText(g).includes(q)), [q]);
  const faqs = useMemo(() => FAQS.filter((f) => !q || plain(`${f.q} ${f.a}`).includes(q)), [q]);
  const openGuide = !q && GUIDES.find((g) => g.id === openId);

  return (
    <div className='ds-page'>
      <Navbar MainProps={props} />
      <PageContainer width='narrow' stack>
        <section className='ds-card help-hero'>
          <div className='help-hero-text'>
            <h1>Help</h1>
            <p className='help-muted'>Short guides with videos, and answers to common questions.</p>
          </div>
          <label className='help-search' htmlFor='help-search'>
            <SearchRoundedIcon />
            <input
              id='help-search'
              type='search'
              placeholder='Search help — e.g. latitude, add loan'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query ? (
              <button type='button' aria-label='Clear search' onClick={() => setQuery('')}><CloseRoundedIcon /></button>
            ) : null}
          </label>
        </section>

        {openGuide ? (
          <GuideView
            guide={openGuide}
            onBack={back}
            onSteps={() => props.history.push(openGuide.page)}
          />
        ) : (
          <>
            <section>
              <h2 className='help-section-title'>How-to guides</h2>
              {guides.length ? (
                <div className='help-grid'>
                  {guides.map((g) => (g.page && g.video ? (
                    <FeaturedCard
                      key={g.id}
                      guide={g}
                      onWatch={() => { setQuery(''); open(g.id); }}
                      onSteps={() => props.history.push(g.page)}
                    />
                  ) : (
                    <GuideCard
                      key={g.id}
                      guide={g}
                      onOpen={() => {
                        if (g.page) { props.history.push(g.page); return; }
                        setQuery(''); open(g.id);
                      }}
                    />
                  )))}
                </div>
              ) : <p className='help-empty'>No guides match your search.</p>}
            </section>

            <section>
              <h2 className='help-section-title'>General questions</h2>
              {faqs.length ? (
                <div className='ds-card help-faqs'>
                  {faqs.map((f) => (
                    <details key={f.q} className='help-faq' open={Boolean(q)}>
                      <summary>
                        <HelpOutlineRoundedIcon className='help-faq-icon' />
                        <span>{f.q}</span>
                        <ExpandMoreRoundedIcon className='help-faq-chevron' />
                      </summary>
                      <p>{rich(f.a)}</p>
                    </details>
                  ))}
                </div>
              ) : <p className='help-empty'>No questions match your search.</p>}
            </section>

            <p className='help-foot help-muted'>Can&apos;t find what you need? Ask your BrickEZ administrator.</p>
          </>
        )}
      </PageContainer>
    </div>
  );
}
